import axios from "axios";
import { useContext } from "react";
import { APIContext } from "../contexts/apiContext";
import { IUploadArtworkResponse } from "./useWebsiteApi";
import { IDesign } from "../types";

export type AddToCartItem = {
  id: number;
  quantity: 1;
  properties: {
    _ID: string;
    Quantity: number;
    Width: string;
    Height: string;
    Material: string;
    Shape: string;
    Files?: string[];
    Instructions?: string;

    _isReorder?: string;
    _approvedArtworkUrl?: string;
    _designCode?: string;
    _proofId?: string;
  };
};

let cartFetcher;

const useShopifyApi = () => {
  const { baseUrl, currencyCode, shipToCountryCode, locale, storeHash } =
    useContext(APIContext);
  const fetcher = (path: string, init: RequestInit) => {
    return fetch(baseUrl + path, init).then((res) => res.json());
  };

  const prepareProduct = (design: IDesign) => {
    const params = new URLSearchParams();
    params.set("w", design.width.toString());
    params.set("h", design.height.toString());
    params.set("u", design.unit);
    params.set("quantity", design.quantity.toString());
    params.set("productSku", design.productSku);
    params.set("materialSku", design.materialSku);
    params.set("currencyCode", currencyCode);
    params.set("shipToCountryCode", shipToCountryCode);
    params.set("languageCode", locale);

    return fetcher(`/v4/shopify/product?${params.toString()}`, {
      method: "POST",
    });
  };

  return {
    fetcher,
    prepareProduct,
    removeFromCart: (cartItemId: string) => {
      return fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          updates: {
            [cartItemId]: 0,
          },
        }),
      });
    },
    addToCart: async (design: IDesign, files?: IUploadArtworkResponse[]) => {
      const item: AddToCartItem = {
        id: 0,
        quantity: 1,
        properties: {
          _ID:
            design._id ||
            "CI" + (new Date().getTime() + Math.random() * 100).toString(),
          Quantity: design.quantity,
          Width: design.width + design.unit,
          Height: design.height + design.unit,
          Material: design.materialSku,
          Shape: design.shape,
          Files: files?.map((f) => f.id),
          Instructions: design.instructions,
        },
      };

      Object.entries(design).forEach(([k, v]) => {
        if (k.startsWith("_")) {
          item.properties[k] = v;
        }
      });

      let variantId: number | undefined = design.variantId;

      if (!variantId) {
        try {
          const product = await prepareProduct(design);
          variantId = parseInt(
            product.variantId.replace("gid://shopify/ProductVariant/", "")
          );
        } catch (err) {
          console.error(err);
          throw err;
        }
      }

      const result = await fetch("/cart/add.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ ...item, id: variantId }),
      });

      let allPricesAreZero = true;

      while (allPricesAreZero) {
        if (!cartFetcher) {
          cartFetcher = fetch("/cart.js").then((resp) => {
            cartFetcher = null;
            return resp.json();
          });
        }

        const cart = await cartFetcher;

        allPricesAreZero = cart.items.some((item) => item.price === 0);
        if (allPricesAreZero) {
          await new Promise<void>((resolve) =>
            setTimeout(() => resolve(), 1000)
          );
        }
      }

      cartFetcher = null;

      if (result.status !== 200) {
        throw new Error("error adding to cart");
      }

      return await result.json();
    },
  };
};

export default useShopifyApi;
